import { useCallback, useState } from "react";

import {
  ProvidersService,
  type CreateProviderCredentialRequest,
  type ProviderCredentialResponse,
  type ProviderType,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError, unwrapNestedErrorMessage } from "@/lib/api-error";
import { ProviderSelect } from "./provider-select";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (data: {
    credential: ProviderCredentialResponse;
    plaintext: string;
  }) => void;
}

const NAME_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9 _.-]{0,199}$/;

export function ProviderCreateDialog({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const [providerType, setProviderType] = useState<ProviderType>("openai");
  const [credentialName, setCredentialName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("");

  const mutator = useCallback(
    (payload: CreateProviderCredentialRequest) =>
      ProvidersService.create(payload),
    []
  );
  const { mutate, isLoading, error, reset } = useMutation<
    CreateProviderCredentialRequest,
    ProviderCredentialResponse
  >(mutator, {
    onSuccess: (credential) => {
      const plaintext = credential.api_key_plaintext ?? "";
      onCreated({ credential, plaintext });
      setCredentialName("");
      setApiKey("");
      setBaseUrl("");
      setProviderType("openai");
    },
  });

  const handleClose = () => {
    reset();
    setCredentialName("");
    setApiKey("");
    setBaseUrl("");
    setProviderType("openai");
    onClose();
  };

  const trimmedName = credentialName.trim();
  const trimmedKey = apiKey.trim();
  const trimmedBaseUrl = baseUrl.trim();

  const nameValid = NAME_PATTERN.test(trimmedName);
  const keyValid = trimmedKey.length >= 8;
  // Tolerate http(s) URLs only; the backend echoes whatever we send,
  // so client-side validation is purely a UX guardrail.
  const baseUrlValid =
    trimmedBaseUrl === "" ||
    /^https?:\/\/[^\s]+$/.test(trimmedBaseUrl);

  const canSubmit = nameValid && keyValid && baseUrlValid && !isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const payload: CreateProviderCredentialRequest = {
      provider_type: providerType,
      credential_name: trimmedName,
      api_key: trimmedKey,
    };
    if (trimmedBaseUrl) payload.base_url = trimmedBaseUrl;
    await mutate(payload);
  };

  return (
    <Dialog open={open} onClose={handleClose} size="max-w-lg">
      <DialogHeader>
        <DialogTitle>{t("providers.createDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("providers.createDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div className="space-y-1.5">
            <label
              htmlFor="provider-type-picker"
              className="block text-sm font-medium text-ocean-900"
            >
              {t("providers.createDialog.providerLabel")}
            </label>
            <ProviderSelect
              id="provider-type-picker"
              value={providerType}
              onChange={setProviderType}
              disabled={isLoading}
              comingSoonLabel={t("providers.createDialog.comingSoon")}
            />
            <p className="text-xs text-neutral-500">
              {providerType === "bedrock"
                ? t("providers.createDialog.providerHintBedrock")
                : t("providers.createDialog.providerHintDefault")}
            </p>
          </div>

          <FormField
            label={t("providers.createDialog.nameLabel")}
            value={credentialName}
            onChange={setCredentialName}
            placeholder={t("providers.createDialog.namePlaceholder")}
            maxLength={200}
            required
            error={
              trimmedName.length > 0 && !nameValid
                ? t("providers.createDialog.nameError")
                : undefined
            }
            hint={t("providers.createDialog.nameHint")}
          />

          <FormField
            label={t("providers.createDialog.keyLabel")}
            type="password"
            value={apiKey}
            onChange={setApiKey}
            placeholder={t("providers.createDialog.keyPlaceholder")}
            required
            error={
              trimmedKey.length > 0 && !keyValid
                ? t("providers.createDialog.keyError")
                : undefined
            }
            hint={t("providers.createDialog.keyHint")}
          />

          <FormField
            label={t("providers.createDialog.baseUrlLabel")}
            value={baseUrl}
            onChange={setBaseUrl}
            placeholder={t("providers.createDialog.baseUrlPlaceholder")}
            maxLength={500}
            error={
              trimmedBaseUrl !== "" && !baseUrlValid
                ? t("providers.createDialog.baseUrlError")
                : undefined
            }
            hint={t("providers.createDialog.baseUrlHint")}
          />

          {error && (
            <Alert variant="danger">
              <div className="space-y-1">
                <div className="text-sm font-medium">
                  {parseApiError(error)}
                </div>
                {/* Surface the upstream provider's own reason kalau
                 *  backend mengembalikan envelope yang dibungkus dua
                 *  kali. Tetap aman: ``unwrapNestedErrorMessage``
                 *  fallback ke raw string. */}
                {error instanceof Error && (
                  <div className="text-xs text-danger-600/80">
                    {t("providers.createDialog.providerDetail", {
                      detail: unwrapNestedErrorMessage(error.message),
                    })}
                  </div>
                )}
              </div>
            </Alert>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("providers.createDialog.cancel")}
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isLoading
              ? t("providers.createDialog.submitLoading")
              : t("providers.createDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
